<template>
  <Dialog modal dismissableMask position="top" style="min-width: 1000px;"
    :closable="false"
    :draggable="false"
    :visible="showingDialog"
    @update:visible="$emit('update:showingDialog', false)"
  >
    <template #header>
      <div style="flex-grow: 1; display: flex; align-items: center; gap: 15px;">
        <ChannelAssignmentsSettingDialogHeader
          :dialogHeader
          :showTopDelete
          :showAggregateOption
          :aggregateChecked
          :setAggregateChecked
          :selectedControl
          :assignedControls
          :deleteControl
          :close
        >
          <template v-if="listedTag">
            <span style="font-weight: bold; font-size: large;">
              {{ bin.name.value }}
            </span>
            <InputText style="text-align: center; font-size: large;"
              :modelValue="listedTag.name.value"
              @update:modelValue="value => setName(value)"
            ></InputText>
            <span style="font-weight: bold; font-size: large;" v-if="container != listedTag">
              {{ container.name.value }}
            </span>
          </template>
        </ChannelAssignmentsSettingDialogHeader>
      </div>
    </template>
    <div v-if="thermostat" style="display: flex; gap: 15px; margin-bottom: 15px;">
      <checkbox v-model="useExternalReference">Use external reference</checkbox>
      <select v-if="useExternalReference" v-model="thermostat.temperatureReference.value">
        <option :value="1">Supply temperature</option>
        <option :value="2">Return temperature</option>
        <option :value="3">Control temperature</option>
      </select>
    </div>
    <div class="dialog-main" v-if="!useExternalReference">
      <div class="channel-picker">
        <div class="bank-picker">
          <Dropdown v-model="selectedController" :options="controllerOptions" optionLabel="label" optionValue="value"></Dropdown>
          <Dropdown v-model="selectedPortNumber" :options="portOptions" optionLabel="label" optionValue="value"></Dropdown>
          <Button v-for="(bank, i) in channelBanks" severity="secondary" :class="{selected: i == selectedBankNumber}" @click="selectedBankNumber = i">
            {{ bank.label }}
          </Button>
        </div>
        <div style="flex-grow: 1;">
          <table>
            <tr v-for="channel in channels">
              <th :class="channelClass(channel)" @click="assignControl(channel)" @dblclick="close()">
                <Button severity="secondary" style="width: 100%;">
                  <div>
                    <div style="font-weight: bold;">
                      {{ channel.channelName }}
                    </div>
                    <div style="font-size: smaller;" v-if="channel.showSensorModel">
                      {{ channel.sensorModel.name.value }}
                    </div>
                  </div>
                </Button>
              </th>
              <td style="width: 100%;">
                <div v-for="control in channel.controls" :class="channelControlClass(control)">
                  {{ controlLabel(control) }}
                </div>
              </td>
            </tr>
          </table>
        </div>
      </div>
      <div class="control-picker" v-if="showControlPicker">
        <div v-for="(control, controlIndex) in allControls" class="control" style="display: flex; gap: 5px"
          :class="{selected: control == selectedControl_, dummyControl: control == dummyControl}" 
          @click="selectControl(control)"
        >
          <InputText style="width: 150px;" v-model="control.name.value" @change="setControlName(control, $event.target.value)"></InputText>
          <Button severity="secondary" class="channelButton">
            {{ controlChannelLabel(control) }}
          </Button>
          <Button v-if="isAssigned(control)" severity="danger" @click="deleteControl(control)" style="min-width: initial;">
            <i class="pi pi-trash"></i>
          </Button>
        </div>
      </div>
    </div>
  </Dialog>

</template>

<script>
import store from "./store.js";
import { last, uniqueBy } from "./utility.js";
import { channelLabel, controlChannelLabel, channelPrefix, isAssigned } from "./appUtility.js";
import { renameChildren, updatePlaceholders, showSubmit, systemCopy } from "./ChannelAssignments.vue";
import ChannelAssignmentsSettingDialogHeader from "./ChannelAssignmentsSettingDialogHeader.vue";
import Tag from "./Tag.js";

// remember the user's selection
let lastBoardId = "localhost";
let lastBoardName = "";
let lastPortNumber = 0;
let lastBankNumber = 0;

export default {
  components: { ChannelAssignmentsSettingDialogHeader },
  props: [
    "showingDialog",
    "title",
    "controllers",
    "channelMap",
    "container",
    "controlsTag",
    "isAuxiliary",
    "selectedControl",
    "row"
  ],
  data() {
    return {
      store,
      renaming: false,
      selectedBoardId: null,
      selectedBoardName: null,
      selectedPortNumber: null,
      selectedBankNumber: null,
      selectedControl_: null,
      aggregateChecked: null, 
      useExternalReference: false,
      dummyControl: null,
    };
  },
  created() {
    if (this.controlsTag)
      this.dummyControl = this.createDummyControl();
    
    let channelNumber = this.selectedControl?.channelNumber.value;
    this.selectedBoardId = (channelNumber == null) ? lastBoardId : this.selectedControl.boardId.value;
    this.selectedBoardName = (channelNumber == null) ? lastBoardName : this.selectedControl.boardName.value
    this.selectedPortNumber = (channelNumber == null) ? lastPortNumber : this.selectedControl.portNumber.value;
    this.selectedBankNumber = (channelNumber == null) ? lastBankNumber : parseInt(channelNumber / 8);
    this.selectControl(this.selectedControl || this.dummyControl);
    this.aggregateChecked = this.assignedControls.length > 1;      
  },
  computed: {
    bin() {
      let bins = systemCopy.value.bins.children;
      return this.container.$parents().find(parent => bins.includes(parent));
    },
    listedTag() {
      let tags = [ ... this.container.$parents(), this.container ];
      return tags.find(tag => tag.$parent?.children && !systemCopy.value.bins.children.includes(tag));
    },
    thermostat() {
      if (this.container.$parent.temperatureReference)
        return this.container.$parent;
      else
        return null;
    },
    dialogHeader() {
      if (this.containerType == "digitalOutputs")
        return this.container.$fullName() + " - " + this.title;
      else
        return this.container.$fullName();
    },
    channelPrefix() {
      return channelPrefix(this.selectedControl_.controlType.value);
    },
    showControlTempOption() {
      return (this.container.sensorType?.value == 0);
    },
    showSelectController() {
      return this.boardsOnSite.length > 1;
    },
    selectedController: {
      get() { 
        let findByBoardId = (controller) => controller.boardId == this.selectedBoardId;
        let findByBoardName = (controller) => controller.boardName == this.selectedBoardName;
        return this.controllers.find(this.selectedBoardId ? findByBoardId : findByBoardName);
      },
      set(controller) {
        this.selectedBoardId = controller.boardId;
        this.selectedBoardName = controller.boardName;
        lastBoardId = this.selectedBoardId;
        lastBoardName = this.selectedBoardName;
      }
    },
    controllerOptions() {
      return this.controllers.map(controller => ({
        value: controller,
        label: `${controller.boardName} ${controller.online ? '' : '(offline)'}`
      }));
    },
    portOptions() {
      return [
        { value: 0, label: "Port E" },
        { value: 1, label: "Port F" },
        { value: 2, label: "Port C" },
        { value: 3, label: "Port D" }
      ];
    },
    containerType() {
      if (this.container.sensors)
        return "aggr";
      else if (this.container.digitalOutputs)
        return "digitalOutputs";
      else if (this.container.children)
        return "digitalOutputs";
      else
        return "single";
    },
    showAggregateOption() {
      return this.containerType == "aggr";
    },
    showControlPicker() {
      return this.controlsTag != null && (this.aggregateChecked || !this.container.sensors);
      // return (
      //   (this.containerType == "humidifier") ||
      //   (this.containerType == "digitalOutputs") ||
      //   (this.containerType == "aggr" && this.aggregateChecked)
      // );
    },
    showTopDelete() {
      return !this.showControlPicker && this.assignedControls.length;
    },
    assignedControls() {
      return this.allControls.filter(control => isAssigned(control));
    },
    allControls() {
      if (this.controlsTag)
        if (this.dummyControl && this.dummyControl != last(this.controlsTag.children))
          return [ ... this.controlsTag.children, this.dummyControl ];
        else
          return this.controlsTag.children;
      else
        return [ this.container ];
    },
    channelType() {
      return this.selectedControl_.controlType.value;
    },
    channelBanks() {
      let banks = [];
      let channelsPerPort = (this.channelType == "ano") ? 12 : 24;
      let channelOffset = this.selectedPortNumber * channelsPerPort;
      let channelPrefix = ({ani: "SAI", ano: "SAO", gpi: "SDI", gpo: "SDO"})[this.channelType];
      for (let i = 0; i < channelsPerPort; i += 8) {
        let numChannels = Math.min(8, channelsPerPort - i);
        banks.push({
          numChannels,
          channelNumber: i,
          label: `${channelPrefix} ${channelOffset + i + 1} - ${channelOffset + i + numChannels}`
        })
      }
      return banks;
    },
    channels() {
      let channels = [];
      let bank = this.channelBanks[this.selectedBankNumber];
      let controller = this.selectedController;
      for (let i = 0; i < bank.numChannels; i++) {
        let channelNumber = bank.channelNumber + i;
        let sensorModel = null;
        if (this.channelType == "ani" && controller.online) {
          let port = controller.ports[this.selectedPortNumber];
          let sensorModelEnum = port.analogInputs.children[channelNumber].sensorModelNumber.value;
          sensorModel = controller.sensorModels[sensorModelEnum];
        }
        let mapEntry = this.channelMap[channelNumber];
        let controls = mapEntry.filter(_control =>
          _control.controlType == this.channelType &&
          _control.boardId == this.selectedBoardId &&
          _control.portNumber == this.selectedPortNumber &&
          _control.channelNumber == channelNumber
        );
        let mappedControls = controls.map(_control => {
          let showSensorType = false;
          if (this.channelType == "ani" && controller.online) {
            if (controller.autoConfigureEnabled) {
              let uniqueSensorTypes = uniqueBy(controls, control => control.sensorType);
              showSensorType = uniqueSensorTypes.length > 1;
            }
            else {
              let aggr = this.selectedControl_.$parent.$parent;
              showSensorType = aggr.sensorType.value != sensorModel.sensorType.value;
            }
          }
          return {
            ... _control,
            showSensorType
          };
        });
        
        channels.push({
          channelName: channelLabel(this.selectedPortNumber, channelNumber, this.channelType),
          boardId: this.selectedBoardId, 
          portNumber: this.selectedPortNumber, 
          channelNumber: channelNumber,
          sensorModel: sensorModel,
          showSensorModel: controller.online && !controller.autoConfigureEnabled && this.channelType == "ani",
          controls: mappedControls
        });
      }

      return channels;
    }
  },
  watch: {
    selectedControl: {
      immediate: true,
      handler() {
        this.selectControl(this.selectedControl);
      }
    },
    selectedPortNumber() { 
      lastPortNumber = this.selectedPortNumber; 
    },
    selectedBankNumber() { 
      lastBankNumber = this.selectedBankNumber; 
    }
  },
  methods: {
    channelClass(channel) {
      return {
        channelHighlight: channel.controls.find(control => {
          return control.handle == this.selectedControl_.handle
        })
      };
    },
    setName(value) {
      this.listedTag.name.value = value;
      showSubmit.value = true;
    },
    setAggregateChecked(value) {
      this.aggregateChecked = value;
      if (!this.aggregateChecked) {
        this.selectControl(this.allControls[0]);
      }
      else if (isAssigned(this.container)) {
        this.dummyControl = this.createDummyControl();
        this.selectControl(this.dummyControl);
      }
    },
    selectControl(control) {
      this.selectedControl_ = control;
      
      // Pausing shortly before emitting the update event helps with performance issues on slower devices.
      setTimeout(() => this.$emit("update:selectedControl", control));
    },
    selectBankFromControl(control) {
      this.selectedBoardId = control.boardId.value;
      this.selectedPortNumber = control.portNumber.value;
      this.selectedBankNumber = Math.floor(control.channelNumber.value / 8);
    },
    createDummyControl() {
      let template = null;
      let templates = this.container.$session.templates;
      switch (this.containerType) {
        case "aggr":
          template = templates.sensor;
          break;
        case "digitalOutputs":
          template = templates.digitalOutput;
          break;
        case "humidifier":
          template = templates.humidifier;
          break;
      }
      if (template) {
        let control = new Tag(store.sessions[0], this.controlsTag, template);
        control.name.value = this.controlsTag.defaultName_multiple.replace("{#}", this.assignedControls.length + 1);
        return control;
      }
      else {
        return null;
      }
    },
    deleteControl(control) {
      showSubmit.value = true;
      if (this.containerType == "single") {
        control.boardId.value = "";
        control.boardName.value = "";
        control.portNumber.value = 0;
        control.channelNumber.value = null;
        this.close();
      }
      else {
        let index = this.controlsTag.children.indexOf(control);
        this.controlsTag.children.splice(index, 1);
        this.dummyControl = this.createDummyControl();
        
        let isControlsInNameOrder = this.assignedControls.every((control, index) => {
          return control.name.value == this.controlsTag.defaultName_multiple.replace("{#}", index + 2);
        });
        if (isControlsInNameOrder) {
          renameChildren(this.allControls, index, true, this.controlsTag.defaultName_single, this.controlsTag.defaultName_multiple);
        }

        if (this.showControlPicker)
          this.selectControl(this.allControls[index]);
        else
          this.close();
      }
    },
    isAssigned(control) {
      return isAssigned(control);
    },
    prevBank() {
      this.selectedBankNumber--;
      if (this.selectedBankNumber < 0) this.selectedBankNumber = 3;
    },
    nextBank() {
      this.selectedBankNumber++;
      if (this.selectedBankNumber > 2) this.selectedBankNumber = 0;
    },
    assignControl(channel) {
      showSubmit.value = true;
      let control = this.selectedControl_;
      control.boardId.value = this.selectedBoardId;
      control.boardName.value = this.selectedBoardName;
      control.portNumber.value = this.selectedPortNumber;
      control.channelNumber.value = channel.channelNumber;
      if (control == this.dummyControl) {
        this.controlsTag.children.push(control);
        this.dummyControl = this.createDummyControl();
        
        // select the next control
        let index = this.assignedControls.indexOf(this.selectedControl_);
        let nextControl = this.allControls[index + 1];
        this.selectControl(nextControl);
      }
      if (!this.showControlPicker) {
        this.close();
      }
    },
    close() {
      lastPortNumber = this.selectedPortNumber;
      lastBankNumber = this.selectedBankNumber;
      updatePlaceholders();
      this.$emit("close");
    },
    onDialogClick(event) {
      if (event.target.tagName != "input")
        this.renaming = false;
    },
    channelsLabel(channelNumberFrom, channelNumberTo) {
      let from = this.selectedPortNumber * 24 + (bankNumber * 8);
      let to = from + 8;
      return `${channelPrefix(this.selectedControl_.controlType.value)} ${from + 1} - ${to}`;
    },
    setControlName(control, name) {
      control.name.value = name;
      showSubmit.value = true;
    },
    controlChannelLabel(control) {
      return controlChannelLabel(control);
    },
    controlLabel(control) {
      if (control.clientName == store.sessions[0].system.boardName.value)
        return control.controlName;
      else
        return `${control.clientName}: ${control.controlName}`;
    },
    containerControlClass(control) {
      return {
        selected: control == this.selectedControl_
      };
    },
    channelControlClass(control) {
      return {
        "channel-control": true,
        highlight: (control.handle == this.selectedControl_.handle)
        // highlight: Boolean(
        //   // control.clientHandle.value == this.selectedController.clientHandle.value &&
        //   control.clientMac.value == system.macAddr.value &&
        //   control.controlHandle.value == this.selectedControl_.handle
        // )
      }
    }
  }
}
</script>

<style scoped>
.p-button {
  min-width: 100px;
  white-space: nowrap;
  justify-content: center;
}
.p-button-secondary {
  border-color: #888;
}
.p-button-danger {
  color: #333;
  background: var(--red-50);
  border: 1px solid var(--red-300);
}
.dialog-main {
  flex-grow: 1;
  display: flex;
  align-items: flex-start;
  gap: 15px;
}
.auxiliary-name {
  width: 145px;
  text-align: center;
  font-size: large;
  font-weight: bold;
  border: 1px solid white;
}

.control-picker {
  padding-left: 15px;
  border-radius: 5px;
  border-left: 1px solid gray;
}
.control-picker,
.bank-picker {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.bank-picker button.selected {
  font-weight: bold;
  border-color: #555;
  background: var(--blue-100);
}
.channel-picker {
  flex-grow: 1;
  display: flex;
  gap: 15px;
}
table {
  width: 100%;
  height: 100%;
  border-collapse: collapse;
}
th, td {
  padding: 10px;
  border: 1px dotted #aaa;
}
td { padding-left: 10px; }
.control.selected button.channelButton {
  font-weight: bold;
  border-color: #555;
  background: var(--blue-100);
}
.control.selected .p-inputtext {
  font-weight: bold;
}
.dummyControl:not(.selected) {
  opacity: 0.6;
}
.dummyControl:not(.selected) input {
  visibility: hidden;
}
.highlight {
  font-weight: bold;
}
</style>
